import React, { useState, useMemo } from 'react';
import { db } from '../data/store';

const fmt = (n) => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}`;
const fmt2 = (n) => `$${(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TYPE_COLORS = {
  Fuel:'#e09900',Specific:'#1a6b42',Water:'#1a7dc4',Coal:'#555',
  Stormwater:'#2196f3',NPDES:'#9c27b0',TCLP:'#f44336',Default:'#1e3060'
};

function KPI({ label, value, sub, color: c, icon, trend, prefix }) {
  return (
    <div style={{background:'white',border:'1px solid #e2e6f0',borderRadius:8,padding:'12px 16px',
      boxShadow:'0 1px 4px rgba(0,0,0,0.06)',borderTop:`3px solid ${c||'#1e3060'}`}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:c||'#1e3060',lineHeight:1.1}}>
            {prefix||''}{value}
          </div>
          <div style={{fontSize:11,color:'#888',marginTop:3,textTransform:'uppercase',letterSpacing:'0.4px'}}>{label}</div>
          {sub && <div style={{fontSize:11,color:'#666',marginTop:2}}>{sub}</div>}
        </div>
        <div style={{fontSize:22,opacity:0.4}}>{icon}</div>
      </div>
      {trend !== undefined && (
        <div style={{marginTop:6,fontSize:11,color:trend>=0?'#1a6b42':'#b83232',fontWeight:600}}>
          {trend>=0?'▲':'▼'} {Math.abs(trend)}% vs last month
        </div>
      )}
    </div>
  );
}

export default function FinanceDashboard({ onNavigate }) {
  const [timeRange, setTimeRange] = useState('12m');
  const [forecastMonths, setForecastMonths] = useState(6);
  const samples = db.getSamples();
  const invoices = db.getInvoices();
  const now = new Date();

  const monthCount = timeRange==='12m'?12:timeRange==='6m'?6:24;

  // ── Core financial metrics ────────────────────────────────────────────────
  const fin = useMemo(() => {
    const billed = samples.filter(s=>s.billed);
    const unbilled = samples.filter(s=>s.completed&&!s.billed);
    const totalRevenue = billed.reduce((a,s)=>a+(s.price||0),0);
    const unbilledAmt = unbilled.reduce((a,s)=>a+(s.price||0),0);
    const outstanding = invoices.filter(i=>i.status==='Outstanding').reduce((a,i)=>a+i.amount,0);
    const paid = invoices.filter(i=>i.status==='Paid').reduce((a,i)=>a+i.amount,0);

    const thisMonth = samples.filter(s=>{
      if(!s.received||!s.billed)return false;
      const d=new Date(s.received);
      return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
    }).reduce((a,s)=>a+(s.price||0),0);

    const lastMonthDate = new Date(now.getFullYear(),now.getMonth()-1,1);
    const lastMonth = samples.filter(s=>{
      if(!s.received||!s.billed)return false;
      const d=new Date(s.received);
      return d.getMonth()===lastMonthDate.getMonth()&&d.getFullYear()===lastMonthDate.getFullYear();
    }).reduce((a,s)=>a+(s.price||0),0);

    const mTrend = lastMonth>0?Math.round((thisMonth-lastMonth)/lastMonth*100):0;
    const avgPerSample = billed.length>0?Math.round(totalRevenue/billed.length):0;
    const collectionRate = (paid+outstanding)>0?Math.round(paid/(paid+outstanding)*100):0;

    return { totalRevenue, unbilledAmt, outstanding, paid, thisMonth, mTrend, avgPerSample, collectionRate, billedCount: billed.length };
  }, [samples, invoices]);

  // ── Monthly revenue data ──────────────────────────────────────────────────
  const monthlyRevenue = useMemo(() => {
    return Array.from({length:monthCount},(_,i)=>{
      const d = new Date(now.getFullYear(),now.getMonth()-(monthCount-1)+i,1);
      const label = MONTHS[d.getMonth()]+(monthCount>12?` '${String(d.getFullYear()).slice(2)}`:'');
      const monthSamples = samples.filter(s=>{
        if(!s.received)return false;
        const sd=new Date(s.received);
        return sd.getMonth()===d.getMonth()&&sd.getFullYear()===d.getFullYear()&&s.billed;
      });
      const revenue = monthSamples.reduce((a,s)=>a+(s.price||0),0);
      const byType = {};
      monthSamples.forEach(s=>{byType[s.testType]=(byType[s.testType]||0)+(s.price||0);});
      return { label, revenue, count: monthSamples.length, byType, month: d.getMonth(), year: d.getFullYear() };
    });
  }, [samples, monthCount]);

  const maxRevenue = Math.max(...monthlyRevenue.map(m=>m.revenue),1);

  // ── Forecast: linear regression on monthly revenue ────────────────────────
  const forecast = useMemo(() => {
    const recent = monthlyRevenue.slice(-6).filter(m=>m.revenue>0);
    if (recent.length < 2) return [];
    const n = recent.length;
    const xs = recent.map((_,i)=>i);
    const ys = recent.map(m=>m.revenue);
    const xMean = xs.reduce((a,x)=>a+x,0)/n;
    const yMean = ys.reduce((a,y)=>a+y,0)/n;
    const slope = xs.reduce((a,x,i)=>a+(x-xMean)*(ys[i]-yMean),0) / xs.reduce((a,x)=>a+(x-xMean)**2,0);
    const intercept = yMean - slope*xMean;

    return Array.from({length:forecastMonths},(_,i)=>{
      const d = new Date(now.getFullYear(),now.getMonth()+1+i,1);
      const val = Math.max(0, Math.round(intercept + slope*(n+i)));
      return { label: MONTHS[d.getMonth()]+(forecastMonths>6?` '${String(d.getFullYear()).slice(2)}`:''), revenue: val, forecast: true };
    });
  }, [monthlyRevenue, forecastMonths]);

  const allChartData = [...monthlyRevenue.slice(-6), ...forecast];
  const chartMax = Math.max(...allChartData.map(m=>m.revenue),1);

  // ── Revenue by client ─────────────────────────────────────────────────────
  const revenueByClient = useMemo(() => {
    const map = {};
    samples.filter(s=>s.billed).forEach(s=>{
      const c=s.client||s.source||'Unknown';
      map[c]=(map[c]||0)+(s.price||0);
    });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,10);
  }, [samples]);
  const maxClientRev = revenueByClient[0]?.[1]||1;
  const totalClientRev = revenueByClient.reduce((a,[,v])=>a+v,0);

  // ── Revenue by test type ──────────────────────────────────────────────────
  const revenueByType = useMemo(() => {
    const map = {};
    samples.filter(s=>s.billed).forEach(s=>{
      map[s.testType]=(map[s.testType]||0)+(s.price||0);
    });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]);
  }, [samples]);
  const totalTypeRev = revenueByType.reduce((a,[,v])=>a+v,0);

  // ── Revenue by assigned person ──────────────────────────────────────────────
  const revenueByPerson = useMemo(()=>{
    const map={};
    samples.filter(s=>s.billed).forEach(s=>{
      const p = s.tech||'Unassigned';
      if(!map[p]) map[p]={ revenue:0, samples:0, invoiced:0, pending:0 };
      map[p].revenue += s.price||0;
      map[p].samples++;
      if(s.billed) map[p].invoiced++;
      else map[p].pending++;
    });
    // Add pending (completed not billed) revenue
    samples.filter(s=>s.completed&&!s.billed).forEach(s=>{
      const p = s.tech||'Unassigned';
      if(!map[p]) map[p]={ revenue:0, samples:0, invoiced:0, pending:0 };
      map[p].pending += s.price||0;
    });
    return Object.entries(map).sort((a,b)=>b[1].revenue-a[1].revenue);
  },[samples]);
  const maxPersonRev = revenueByPerson[0]?.[1]?.revenue||1;
  const totalPersonRev = revenueByPerson.reduce((a,[,d])=>a+d.revenue,0);

  // ── Invoice aging ─────────────────────────────────────────────────────────
  const aging = useMemo(() => {
    const outstanding = invoices.filter(i=>i.status==='Outstanding');
    const buckets = {'0-30':0,'31-60':0,'61-90':0,'90+':0};
    outstanding.forEach(inv=>{
      const days = Math.floor((now-new Date(inv.date))/86400000);
      if(days<=30) buckets['0-30']+=inv.amount;
      else if(days<=60) buckets['31-60']+=inv.amount;
      else if(days<=90) buckets['61-90']+=inv.amount;
      else buckets['90+']+=inv.amount;
    });
    return buckets;
  }, [invoices]);

  // ── Growth metrics ────────────────────────────────────────────────────────
  const growth = useMemo(() => {
    const recent6 = monthlyRevenue.slice(-6).reduce((a,m)=>a+m.revenue,0);
    const prior6 = monthlyRevenue.slice(-12,-6).reduce((a,m)=>a+m.revenue,0);
    const g6 = prior6>0?Math.round((recent6-prior6)/prior6*100):0;
    const ytd = samples.filter(s=>{
      return s.billed&&s.received&&new Date(s.received).getFullYear()===now.getFullYear();
    }).reduce((a,s)=>a+(s.price||0),0);
    const priorYtd = samples.filter(s=>{
      return s.billed&&s.received&&new Date(s.received).getFullYear()===now.getFullYear()-1;
    }).reduce((a,s)=>a+(s.price||0),0);
    const gYtd = priorYtd>0?Math.round((ytd-priorYtd)/priorYtd*100):0;
    const forecastAnnual = forecast.length>0
      ? Math.round(recent6/6*12)
      : Math.round(monthlyRevenue.slice(-3).reduce((a,m)=>a+m.revenue,0)/3*12);
    return { recent6, prior6, g6, ytd, priorYtd, gYtd, forecastAnnual };
  }, [monthlyRevenue, samples, forecast]);

  const cardStyle = {background:'white',border:'1px solid #e2e6f0',borderRadius:8,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.05)'};
  const hdr = (label,sub) => (
    <div style={{background:'#1e3060',color:'white',padding:'8px 14px',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
      <span>{label}</span>
      {sub&&<span style={{fontSize:10,fontWeight:400,color:'#aac4e0'}}>{sub}</span>}
    </div>
  );

  return (
    <div style={{overflowY:'auto',height:'100%',padding:'14px 16px',background:'#eef0f5'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
        <div>
          <div style={{fontSize:18,fontWeight:700,color:'#1e3060'}}>Finance Dashboard</div>
          <div style={{fontSize:11,color:'#888'}}>Revenue analytics, invoice status & growth projections</div>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:6}}>
          {[['6m','6 Mo'],['12m','12 Mo'],['24m','2 Yr']].map(([v,l])=>(
            <button key={v} onClick={()=>setTimeRange(v)}
              style={{padding:'4px 12px',borderRadius:4,border:'1px solid #cdd3e4',fontSize:11,cursor:'pointer',fontWeight:600,
                background:timeRange===v?'#1e3060':'white',color:timeRange===v?'white':'#1e3060'}}>
              {l}
            </button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={()=>onNavigate('invoice')}>💰 Invoicing</button>
      </div>

      {/* KPI row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(148px,1fr))',gap:10,marginBottom:14}}>
        <KPI label="Total Revenue" value={fmt(fin.totalRevenue)} icon="💰" color="#1a6b42"/>
        <KPI label="This Month" value={fmt(fin.thisMonth)} icon="📅" color="#1e3060" trend={fin.mTrend}/>
        <KPI label="Outstanding" value={fmt(fin.outstanding)} icon="⏳" color="#b83232"/>
        <KPI label="Total Paid" value={fmt(fin.paid)} icon="✅" color="#1a6b42"/>
        <KPI label="Unbilled" value={fmt(fin.unbilledAmt)} icon="📋" color="#e09900"/>
        <KPI label="Avg / Sample" value={fmt(fin.avgPerSample)} icon="🔬" color="#9c27b0"/>
        <KPI label="Collection Rate" value={`${fin.collectionRate}%`} icon="📊" color={fin.collectionRate>=80?'#1a6b42':'#b83232'}/>
      </div>

      {/* Revenue chart + forecast */}
      <div style={{...cardStyle,marginBottom:12}}>
        {hdr('Revenue Trend & Forecast',
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:10,color:'#aac4e0'}}>Forecast:</span>
            {[3,6,12].map(m=>(
              <button key={m} onClick={()=>setForecastMonths(m)}
                style={{padding:'1px 8px',borderRadius:3,border:'1px solid rgba(255,255,255,0.3)',
                  background:forecastMonths===m?'rgba(255,255,255,0.2)':'none',
                  color:'white',fontSize:10,cursor:'pointer'}}>
                +{m}mo
              </button>
            ))}
          </div>
        )}
        <div style={{padding:'14px 16px'}}>
          {/* Combined chart */}
          <div style={{display:'flex',alignItems:'flex-end',gap:6,height:160}}>
            {/* Historical bars */}
            {monthlyRevenue.map((m,i)=>{
              const h = Math.max(Math.round(m.revenue/chartMax*130),m.revenue>0?6:0);
              return (
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                  <span style={{fontSize:9,fontWeight:700,color:'#1e3060',minHeight:12}}>
                    {m.revenue>0?`${fmt(m.revenue).replace('$','$')}`:''}</span>
                  <div title={`${m.label}: ${fmt(m.revenue)}`}
                    style={{width:'100%',height:`${h}px`,background:'#1e3060',borderRadius:'3px 3px 0 0',
                      transition:'height 0.5s',minHeight:m.revenue>0?4:0,
                      boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}/>
                  <span style={{fontSize:9,color:'#888'}}>{m.label}</span>
                </div>
              );
            })}
            {/* Separator */}
            {forecast.length>0&&<div style={{width:2,height:140,background:'#e09900',opacity:0.6,borderRadius:1,flexShrink:0,alignSelf:'flex-end'}}/>}
            {/* Forecast bars */}
            {forecast.map((m,i)=>{
              const h = Math.max(Math.round(m.revenue/chartMax*130),m.revenue>0?4:0);
              return (
                <div key={`f${i}`} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                  <span style={{fontSize:9,fontWeight:700,color:'#e09900',minHeight:12}}>
                    {m.revenue>0?fmt(m.revenue):''}</span>
                  <div title={`Forecast ${m.label}: ${fmt(m.revenue)}`}
                    style={{width:'100%',height:`${h}px`,
                      background:'repeating-linear-gradient(45deg,#e09900,#e09900 4px,#fff8e0 4px,#fff8e0 8px)',
                      borderRadius:'3px 3px 0 0',border:'1px dashed #e09900',minHeight:m.revenue>0?4:0}}/>
                  <span style={{fontSize:9,color:'#e09900',fontWeight:600}}>{m.label}</span>
                </div>
              );
            })}
          </div>
          <div style={{display:'flex',gap:16,marginTop:8,fontSize:11}}>
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <div style={{width:12,height:12,borderRadius:2,background:'#1e3060'}}/><span style={{color:'#555'}}>Actual revenue</span>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <div style={{width:12,height:12,borderRadius:2,background:'#e09900',opacity:0.7}}/><span style={{color:'#555'}}>Forecast (linear trend)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Revenue by client + by type */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div style={cardStyle}>
          {hdr('Revenue by Client', 'top 10')}
          <div style={{padding:'10px 14px'}}>
            {revenueByClient.map(([c,v])=>(
              <div key={c} style={{marginBottom:6}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:2}}>
                  <span style={{maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#333'}} title={c}>{c}</span>
                  <span style={{fontWeight:700,color:'#1a6b42',flexShrink:0,marginLeft:8}}>{fmt(v)} <span style={{color:'#aaa',fontWeight:400}}>({Math.round(v/totalClientRev*100)}%)</span></span>
                </div>
                <div style={{background:'#f0f2f8',borderRadius:10,height:8}}>
                  <div style={{height:'100%',width:`${Math.round(v/maxClientRev*100)}%`,background:'#1a6b42',borderRadius:10}}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={cardStyle}>
            {hdr('Revenue by Test Type')}
            <div style={{padding:'10px 14px'}}>
              {revenueByType.map(([t,v])=>{
                const col = TYPE_COLORS[t]||TYPE_COLORS.Default;
                return (
                  <div key={t} style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                    <div style={{width:10,height:10,borderRadius:2,background:col,flexShrink:0}}/>
                    <span style={{flex:1,fontSize:11,color:'#333'}}>{t}</span>
                    <span style={{fontSize:11,fontWeight:700,color:col}}>{fmt(v)}</span>
                    <span style={{fontSize:10,color:'#aaa',width:30,textAlign:'right'}}>{Math.round(v/totalTypeRev*100)}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invoice aging */}
          <div style={cardStyle}>
            {hdr('Invoice Aging — Outstanding')}
            <div style={{padding:'10px 14px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {Object.entries(aging).map(([bucket,amt])=>{
                const col = bucket==='0-30'?'#1a6b42':bucket==='31-60'?'#e09900':bucket==='61-90'?'#e05500':'#b83232';
                return (
                  <div key={bucket} style={{background:'#f8f9ff',borderRadius:6,padding:'8px 10px',
                    border:`1px solid ${col}44`,borderLeft:`3px solid ${col}`}}>
                    <div style={{fontSize:15,fontWeight:700,color:col}}>{fmt(amt)}</div>
                    <div style={{fontSize:10,color:'#888',marginTop:2}}>{bucket} days</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2b: Revenue by Assigned Person */}
      <div style={{...cardStyle,marginBottom:12}}>
        {hdr('👤 Revenue by Assigned Person', 'billed samples')}
        <div style={{padding:'12px 14px',overflowX:'auto'}}>
          {revenueByPerson.length===0 ? (
            <div style={{color:'#999',fontSize:12,textAlign:'center',padding:12}}>No billed samples</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{background:'#f0f4ff'}}>
                  {['Person','Billed Samples','Revenue Generated','Share','Pending Revenue','Revenue Bar'].map(h=>(
                    <th key={h} style={{padding:'5px 10px',borderBottom:'1px solid #e2e6f0',
                      color:'#1e3060',fontWeight:600,fontSize:11,
                      textAlign:h==='Person'?'left':'center'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {revenueByPerson.map(([name,d],i)=>(
                  <tr key={name} style={{borderBottom:'1px solid #f0f2f8',
                    background:i%2===0?'white':'#fafbff'}}>
                    <td style={{padding:'6px 10px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:'#1a6b42',
                          color:'white',fontSize:12,fontWeight:700,display:'flex',
                          alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {name[0]?.toUpperCase()}
                        </div>
                        <span style={{fontWeight:600,color:'#1e3060'}}>{name}</span>
                      </div>
                    </td>
                    <td style={{padding:'6px 10px',textAlign:'center',fontWeight:600}}>{d.samples}</td>
                    <td style={{padding:'6px 10px',textAlign:'center',fontWeight:700,
                      color:'#1a6b42',fontSize:13}}>{fmt(d.revenue)}</td>
                    <td style={{padding:'6px 10px',textAlign:'center',fontSize:11,color:'#888'}}>
                      {totalPersonRev>0?Math.round(d.revenue/totalPersonRev*100):0}%
                    </td>
                    <td style={{padding:'6px 10px',textAlign:'center',
                      color:'#e09900',fontWeight:600,fontSize:11}}>
                      {typeof d.pending==='number'&&d.pending>0 ? fmt(d.pending) : '—'}
                    </td>
                    <td style={{padding:'6px 10px',minWidth:120}}>
                      <div style={{background:'#f0f2f8',borderRadius:10,height:10,overflow:'hidden'}}>
                        <div style={{height:'100%',
                          width:`${Math.round(d.revenue/maxPersonRev*100)}%`,
                          background:'linear-gradient(90deg,#1a6b42,#2ecc71)',
                          borderRadius:10,transition:'width 0.5s'}}/>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{background:'#f0f4ff',fontWeight:700}}>
                  <td style={{padding:'6px 10px',color:'#1e3060'}}>Total</td>
                  <td style={{padding:'6px 10px',textAlign:'center'}}>
                    {revenueByPerson.reduce((a,[,d])=>a+d.samples,0)}
                  </td>
                  <td style={{padding:'6px 10px',textAlign:'center',color:'#1a6b42',fontSize:13}}>
                    {fmt(totalPersonRev)}
                  </td>
                  <td style={{padding:'6px 10px',textAlign:'center'}}>100%</td>
                  <td colSpan={2}/>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Row 3: Growth analysis */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>

        {/* 6-month comparison */}
        <div style={cardStyle}>
          {hdr('6-Month Comparison')}
          <div style={{padding:'14px'}}>
            <div style={{display:'flex',alignItems:'flex-end',gap:16,justifyContent:'center',marginBottom:12}}>
              {[
                {label:'Last 6 Mo',val:growth.prior6,col:'#aab8cc'},
                {label:'Recent 6 Mo',val:growth.recent6,col:'#1e3060'},
              ].map(({label,val,col})=>{
                const max = Math.max(growth.prior6,growth.recent6,1);
                const h = Math.round(val/max*80);
                return (
                  <div key={label} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                    <span style={{fontSize:12,fontWeight:700,color:col}}>{fmt(val)}</span>
                    <div style={{width:'100%',height:`${h}px`,background:col,borderRadius:'4px 4px 0 0',minHeight:8,transition:'height 0.5s'}}/>
                    <span style={{fontSize:10,color:'#888',textAlign:'center'}}>{label}</span>
                  </div>
                );
              })}
            </div>
            <div style={{textAlign:'center',padding:'8px',borderRadius:6,
              background:growth.g6>=0?'#d4edda':'#f8d7da'}}>
              <div style={{fontSize:20,fontWeight:700,color:growth.g6>=0?'#1a6b42':'#b83232'}}>
                {growth.g6>=0?'▲':'▼'} {Math.abs(growth.g6)}%
              </div>
              <div style={{fontSize:11,color:'#555'}}>Period-over-period growth</div>
            </div>
          </div>
        </div>

        {/* YTD */}
        <div style={cardStyle}>
          {hdr('Year-to-Date Revenue')}
          <div style={{padding:'14px'}}>
            <div style={{display:'flex',alignItems:'flex-end',gap:16,justifyContent:'center',marginBottom:12}}>
              {[
                {label:`${now.getFullYear()-1} YTD`,val:growth.priorYtd,col:'#aab8cc'},
                {label:`${now.getFullYear()} YTD`,val:growth.ytd,col:'#1a6b42'},
              ].map(({label,val,col})=>{
                const max = Math.max(growth.priorYtd,growth.ytd,1);
                const h = Math.round(val/max*80);
                return (
                  <div key={label} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                    <span style={{fontSize:12,fontWeight:700,color:col}}>{fmt(val)}</span>
                    <div style={{width:'100%',height:`${h}px`,background:col,borderRadius:'4px 4px 0 0',minHeight:8,transition:'height 0.5s'}}/>
                    <span style={{fontSize:10,color:'#888',textAlign:'center'}}>{label}</span>
                  </div>
                );
              })}
            </div>
            <div style={{textAlign:'center',padding:'8px',borderRadius:6,
              background:growth.gYtd>=0?'#d4edda':'#f8d7da'}}>
              <div style={{fontSize:20,fontWeight:700,color:growth.gYtd>=0?'#1a6b42':'#b83232'}}>
                {growth.gYtd>=0?'▲':'▼'} {Math.abs(growth.gYtd)}%
              </div>
              <div style={{fontSize:11,color:'#555'}}>Year-over-year</div>
            </div>
          </div>
        </div>

        {/* Annual forecast */}
        <div style={cardStyle}>
          {hdr('Annual Revenue Forecast')}
          <div style={{padding:'14px'}}>
            <div style={{textAlign:'center',marginBottom:12}}>
              <div style={{fontSize:11,color:'#888',marginBottom:4}}>Projected Annual Run Rate</div>
              <div style={{fontSize:28,fontWeight:700,color:'#1e3060'}}>{fmt(growth.forecastAnnual)}</div>
              <div style={{fontSize:11,color:'#666',marginTop:2}}>Based on recent 3-month average × 12</div>
            </div>
            <div style={{background:'#f0f4ff',borderRadius:6,padding:10}}>
              <div style={{fontSize:11,fontWeight:700,color:'#1e3060',marginBottom:6}}>Growth Scenarios</div>
              {[
                {label:'Conservative (−10%)', val: Math.round(growth.forecastAnnual*0.9), col:'#b83232'},
                {label:'Base Case',           val: growth.forecastAnnual,                col:'#1e3060'},
                {label:'Optimistic (+20%)',   val: Math.round(growth.forecastAnnual*1.2), col:'#1a6b42'},
                {label:'Growth (+40%)',       val: Math.round(growth.forecastAnnual*1.4), col:'#9c27b0'},
              ].map(({label,val,col})=>(
                <div key={label} style={{display:'flex',justifyContent:'space-between',fontSize:11,
                  padding:'4px 0',borderBottom:'1px solid #e0e4ec'}}>
                  <span style={{color:'#555'}}>{label}</span>
                  <span style={{fontWeight:700,color:col}}>{fmt(val)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 4: Recent invoices table */}
      <div style={cardStyle}>
        {hdr('Recent Invoices', `${invoices.filter(i=>i.status==='Outstanding').length} outstanding`)}
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead>
              <tr style={{background:'#f0f4ff'}}>
                {['Invoice #','Client','Samples','Amount','Date','Due Date','Status','Aging'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid #e2e6f0',color:'#1e3060',fontWeight:600}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...invoices].sort((a,b)=>b.num.localeCompare(a.num)).slice(0,12).map(inv=>{
                const days = Math.floor((now-new Date(inv.date))/86400000);
                const overdue = inv.status==='Outstanding' && inv.dueDate && new Date(inv.dueDate)<now;
                return (
                  <tr key={inv.num} style={{borderBottom:'1px solid #f0f2f8',background:overdue?'#fff5f5':'white'}}>
                    <td style={{padding:'5px 10px',fontWeight:700,fontFamily:'monospace'}}>#{inv.num}</td>
                    <td style={{padding:'5px 10px',maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{inv.client}</td>
                    <td style={{padding:'5px 10px',textAlign:'center'}}>{inv.sampleIds?.length||0}</td>
                    <td style={{padding:'5px 10px',fontWeight:700,color:'#1a6b42'}}>{fmt2(inv.amount)}</td>
                    <td style={{padding:'5px 10px',color:'#666'}}>{inv.date}</td>
                    <td style={{padding:'5px 10px',color:overdue?'#b83232':'#666',fontWeight:overdue?700:400}}>
                      {inv.dueDate||'—'}{overdue?' ⚠️':''}
                    </td>
                    <td style={{padding:'5px 10px'}}>
                      <span style={{background:inv.status==='Paid'?'#d4edda':'#fff3cd',
                        color:inv.status==='Paid'?'#155724':'#856404',
                        padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:700}}>
                        {inv.status}
                      </span>
                    </td>
                    <td style={{padding:'5px 10px',color:days>90?'#b83232':days>60?'#e05500':days>30?'#e09900':'#1a6b42',fontWeight:600}}>
                      {inv.status==='Paid'?'Paid':`${days}d`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
